import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UserViewSegment from '../Profile/components/UserViewSegment';
import useDebounce from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useAdsListInfiniteQuery } from '../../../hooks/useAdsQuery';
import { emptyListDescription } from './UserAnnouncements+Data';
import { toUTCStartOfDay, toUTCEndOfDay } from '../../../helpers/dates';
import { AdsListStatus, AdsResponse } from '../../../types/api/ads';
import AdsFilterFormModal from './components/FilterModal/AdsFilterFormModal';
import AdsCard from './components/AdsCard';
import { pageSize } from '../../../services/settings';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import { Placeholder, createErrorPlaceholder } from '../../basic';
import { Stack, TextInput, Button, SkeletonBox } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon } from '@primer/octicons-react'

export default function UserAnnouncements() {

    const navigate = useNavigate();
    const adsListRef = useRef<HTMLDivElement | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const rawId = searchParams.get('id');
    const userId = useMemo(() => rawId || undefined, [rawId]);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)
    const [selectedFilter, setSelectedFilter] = useState<AdsListStatus | undefined>(undefined);
    const [filterOpened, setFilterOpened] = useState<boolean>(false);


    // Filters Modal
    const [priceFilterOpened, setPriceFilterOpened] = useState<boolean>(false);
    const [dateFilterOpened, setDateFilterOpened] = useState<boolean>(false);

    // Filter request params
    const [fromPrice, setFromPrice] = useState<string>('');
    const [toPrice, setToPrice] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Filter draft params for modal
    const [fromPriceDraft, setFromPriceDraft] = useState<string>('');
    const [toPriceDraft, setToPriceDraft] = useState<string>('');
    const [fromDateDraft, setFromDateDraft] = useState<string>('');
    const [toDateDraft, setToDateDraft] = useState<string>('');

    // Prepare filter request params
    const fromPriceNumber = parseFloat(fromPrice);
    const toPriceNumber = parseFloat(toPrice);

    const createdFromIsoDate = fromDate ? toUTCStartOfDay(fromDate) : undefined;
    const createdToIsoDate = toDate ? toUTCEndOfDay(toDate) : undefined;

    const priceFrom = isNaN(fromPriceNumber) ? undefined : fromPriceNumber;
    const priceTo = isNaN(toPriceNumber) ? undefined : toPriceNumber;

    const {
        isLoading: isLoadingAds,
        data: adsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useAdsListInfiniteQuery(
        debouncedSearchQuery, // search
        pageSize, //limit
        undefined, // isActive
        "created_at", // sort
        "desc", // ordering
        selectedFilter, // status
        createdFromIsoDate, // createdFrom
        createdToIsoDate, // createdTo
        priceFrom, // priceFrom
        priceTo, // priceTo,
        userId,
        undefined,
        undefined,
        {
            enabled: !!userId
        }
    );

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    useEffect(() => {
        if (!filterOpened) {
            // Сброс изменений, если закрыли кликом по фону
            setFromPriceDraft(fromPrice);
            setToPriceDraft(toPrice);
            setFromDateDraft(fromDate);
            setToDateDraft(toDate);
        }
    }, [filterOpened]);

    const handleScroll = useInfiniteScroll(
        adsListRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        adsData
    );

    const handleDismiss = () => {
        setFromPrice('');
        setToPrice('');
        setFromDate('');
        setToDate('');

        setFromPriceDraft('');
        setToPriceDraft('');
        setFromDateDraft('');
        setToDateDraft('');

        setFilterOpened(false);
    }
    const handleApply = () => {
        setFromPrice(fromPriceDraft);
        setToPrice(toPriceDraft);
        setFromDate(fromDateDraft);
        setToDate(toDateDraft);

        setFilterOpened(false);
    }

    const openAdsPage = (id: string) => {
        navigate('/ads/ad?id=' + id)
    }

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <UserViewSegment id={searchParams.get('id') || ''} />
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />

                        <AdsFilterFormModal
                            filterOpened={filterOpened}
                            setFilterOpened={setFilterOpened}

                            priceFilterOpened={priceFilterOpened}
                            setPriceFilterOpened={setPriceFilterOpened}

                            dateFilterOpened={dateFilterOpened}
                            setDateFilterOpened={setDateFilterOpened}

                            selectedFilter={selectedFilter}
                            setSelectedFilter={setSelectedFilter}

                            fromPrice={fromPriceDraft}
                            setFromPrice={setFromPriceDraft}
                            toPrice={toPriceDraft}
                            setToPrice={setToPriceDraft}

                            fromDate={fromDateDraft}
                            setFromDate={setFromDateDraft}
                            toDate={toDateDraft}
                            setToDate={setToDateDraft}

                            onDismiss={handleDismiss}
                            onApply={handleApply}
                        />
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    <div
                        ref={adsListRef}
                        onScroll={handleScroll}
                        className="flex flex-col md:grid md:grid-cols-2 gap-4 max-h-screen overflow-y-auto"
                    >
                        {adsData?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: AdsResponse, index) => (
                                    <AdsCard
                                        key={e.id}
                                        ads={e}
                                        onOpenClick={() => openAdsPage(e.id)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    {!isLoadingAds && !isFetchingNextPage && (adsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoadingAds) &&
                        <div className="flex flex-col md:grid md:grid-cols-2 gap-4 max-h-screen overflow-y-auto">
                            <SkeletonBox height="4rem" />
                            <SkeletonBox height="4rem" />
                        </div>
                    }
                </Stack>
            }
        ></RegularPageLayout>

    )
}